import { useMutation } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popper,
  Stack,
  Typography,
  styled,
  useColorScheme,
  useTheme,
} from "@mui/material";
import { DetectiveIcon } from "@phosphor-icons/react/dist/csr/Detective";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { SignOutIcon } from "@phosphor-icons/react/dist/csr/SignOut";

import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Permissions from "../../constants/permissions";
import UserPreferences from "../../constants/userPreferences";
import useAuth from "../../context/UserContext";
import { ResetAuth0PasswordDocument } from "../../graphql/types";
import { usePreferences } from "../../hooks/usePreferences";
import { requireToken } from "../../strict/lib/authErrors";
import CustomToggleButton from "../CustomToggleButton";
import { IfPermission } from "../Permissions";
import { UserAvatar } from "../ui/UserAvatar";

const UserMenuText = styled(ListItemText)({
  "& .MuiTypography-root": { fontSize: "14px" },
});

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.drawer,
}));

const PopperContainer = styled(Stack)(({ theme }) => ({
  borderRadius: "4px",
  background: theme.palette.surfaceLight10.main,
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    boxShadow: theme.mixins.shadowSurfaceDark10,
  }),
  padding: "20px",
}));

export const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { logout: auth0Logout, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { mode } = useColorScheme();
  const theme = useTheme();
  const { savePreference } = usePreferences({});
  const [pwdLogin, setPwdLogin] = useState<boolean>(true);

  const { user, error } = useAuth();
  if (error) {
    console.error("logout unsuccessful - refresh page.", error);
  }
  const handleClick = (e: React.MouseEvent<HTMLElement>) =>
    anchorEl ? setAnchorEl(null) : setAnchorEl(e.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const [resetPassword] = useMutation(ResetAuth0PasswordDocument, {
    onCompleted: () => {
      toast.success(`Password reset email sent to ${user.email}`);
    },
    onError: () => {
      toast.error("Error sending password reset email");
    },
  });

  const onChangePassword = () => {
    setAnchorEl(null);
    resetPassword({
      variables: {
        email: user.email,
      },
    });
  };

  const handleClickMode = (mode: "light" | "dark" | "system") => {
    savePreference(UserPreferences.Mode, mode);
  };

  const handleLogout = () => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  useEffect(() => {
    const isPwdLogin = async () => {
      const accessToken = requireToken(await getAccessTokenSilently());
      const subject = jwtDecode<{ sub: string }>(accessToken);
      setPwdLogin(subject.sub.split("|")[0] === "auth0");
    };

    isPwdLogin();
  }, [getAccessTokenSilently]);
  return (
    <>
      <IconButton onClick={handleClick} data-testid="user-menu-icon" sx={{ padding: 0 }}>
        <UserAvatar size="medium" name={user.name} />
      </IconButton>
      <StyledPopper open={open} anchorEl={anchorEl} placement="bottom-end">
        <PopperContainer>
          <ClickAwayListener onClickAway={handleClose}>
            <Stack sx={{ gap: "17px" }}>
              <Stack data-cy="user-details">
                <UserAvatar size="medium" name={user.name} />
                <Typography variant="heading.seven" data-testid="user-name">
                  {user.name}
                </Typography>
                <Typography variant="body.medium.regular">{user.email}</Typography>
              </Stack>
              <CustomToggleButton
                buttonContent={[
                  {
                    title: "Light mode",
                    selected: mode === "light",
                    onClick: () => handleClickMode("light"),
                  },
                  {
                    title: "Dark mode",
                    selected: mode === "dark",
                    onClick: () => handleClickMode("dark"),
                  },
                  {
                    title: "System",
                    selected: mode === "system",
                    onClick: () => handleClickMode("system"),
                  },
                ]}
              />
              <Divider />
              <List data-cy="user-menu">
                <IfPermission permission={Permissions.ListAccountsV2}>
                  <ListItemButton
                    onClick={() => {
                      navigate("/admin");
                    }}
                    data-cy="admin"
                  >
                    <Stack direction="row" sx={{ gap: "8px", alignItems: "center" }}>
                      <DetectiveIcon color={theme.palette.text40.main} size="24px" />
                      <UserMenuText primary="Admin" />
                    </Stack>
                  </ListItemButton>
                </IfPermission>
                {pwdLogin && (
                  <ListItemButton onClick={onChangePassword} data-cy="change-password">
                    <Stack sx={{ gap: "8px", alignItems: "center" }} direction="row">
                      <KeyIcon color={theme.palette.text40.main} size="24px" />
                      <UserMenuText primary="Reset password" />
                    </Stack>
                  </ListItemButton>
                )}
                <ListItemButton onClick={handleLogout} data-cy="logout">
                  <Stack sx={{ gap: "8px", alignItems: "center" }} direction="row">
                    <SignOutIcon color={theme.palette.text40.main} size="24px" />
                    <UserMenuText primary="Logout" />
                  </Stack>
                </ListItemButton>
              </List>
            </Stack>
          </ClickAwayListener>
        </PopperContainer>
      </StyledPopper>
    </>
  );
};
